/**
 * FinancialTransactions.js
 * Transactions tab — view, filter, re-map type/category, and split transactions.
 *
 * API calls:
 *   GET   /fin-remap/transactions              — paginated list
 *   PATCH /fin-remap/transactions/{id}         — save one row
 *   POST  /fin-remap/transactions/bulk-update  — save all dirty rows
 *   POST  /fin-remap/transactions/{id}/split   — split one transaction into N lines
 *   GET   /fin-pnl/categories                  — category list
 *   GET   /fin-remap/bank-sources              — bank list
 *   GET   /fin-pnl/years                       — year list
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "./apiClient";
import { useTheme, getT, glassCard } from "./ThemeContext";
import ExcelDownloadButton from "./components/ExcelDownloadButton";
import FinancialSelect from "./FinancialSelect";
import { ALL_CATEGORIES } from "./constants/financialCategories";
import { useAppConfig } from "./AppContext";
import { useDateTimeFormat } from "./utils/formatters";

const PAGE_SIZE = 100;

const IE_OPTIONS = ["Income","Expense","Equity"];

// ── tiny helpers ──────────────────────────────────────────────────────────────

function Spinner({ T, size = 32 }) {
  return (
    <span style={{
      display:"inline-block", width:size, height:size, borderRadius:"50%",
      border:"2px solid "+T.divider, borderTopColor:T.yellow,
      animation:"fin-spin 0.7s linear infinite",
    }}>
      <style>{`@keyframes fin-spin{to{transform:rotate(360deg)}}`}</style>
    </span>
  );
}

function FilterSelect({ value, onChange, options, placeholder, T, minW = 110 }) {
  return (
    <FinancialSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      T={T}
      style={{ minWidth: minW }}
    />
  );
}

// ── main component ────────────────────────────────────────────────────────────

export default function FinancialTransactions() {
  const { isDark } = useTheme();
  const T  = getT(isDark);
  const gc = glassCard(T);
  const { config } = useAppConfig();
  const { fmtDate } = useDateTimeFormat();
  const fmt = (n) =>
    Number(n).toLocaleString("en-US", { style: "currency", currency: config.currency || "USD" });

  // ── filter state ─────────────────────────────────────────────────────────
  const [years,       setYears]       = useState([]);
  const [banks,       setBanks]       = useState([]);
  const [categories,  setCategories]  = useState(ALL_CATEGORIES);

  const [fYear,   setFYear]   = useState("");
  const [fMonth,  setFMonth]  = useState("");
  const [fBank,   setFBank]   = useState("");
  const [fType,   setFType]   = useState("");
  const [fCat,    setFCat]    = useState("");
  const [fSearch, setFSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");   // uncontrolled until Enter

  // ── data + pagination ─────────────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  // ── inline edit state ─────────────────────────────────────────────────────
  // dirty[id] = { income_expense, category } — tracks unsaved changes
  const [dirty,     setDirty]     = useState({});
  const [saving,    setSaving]    = useState(false);
  const [saveMsg,   setSaveMsg]   = useState(null);

  // ── sort state ───────────────────────────────────────────────────────────
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (col) => {
    setSortDir(prev => (sortKey === col ? (prev === "asc" ? "desc" : "asc") : "asc"));
    setSortKey(col);
  };

  const sortedRows = [...rows].sort((a, b) => {
    let av, bv;
    if      (sortKey === "date")     { av = a.transaction_date; bv = b.transaction_date; }
    else if (sortKey === "amount")   { av = Number(a.amount);   bv = Number(b.amount); }
    else if (sortKey === "bank")     { av = (a.bank_source || "").toLowerCase();    bv = (b.bank_source || "").toLowerCase(); }
    else if (sortKey === "type")     { av = (a.income_expense || "").toLowerCase(); bv = (b.income_expense || "").toLowerCase(); }
    else if (sortKey === "category") { av = (a.category || "").toLowerCase();       bv = (b.category || "").toLowerCase(); }
    else if (sortKey === "description") { av = (a.description || "").toLowerCase(); bv = (b.description || "").toLowerCase(); }
    else return 0;
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // ── split state ───────────────────────────────────────────────────────────
  const [splitId,     setSplitId]     = useState(null);   // id of row being split
  const [splitLines,  setSplitLines]  = useState([]);     // [{key, amount, income_expense, category, description}]
  const [splitSaving, setSplitSaving] = useState(false);
  const [splitError,  setSplitError]  = useState("");

  // ── load dropdown options once ────────────────────────────────────────────
  useEffect(() => {
    api.get("/fin-pnl/years").then(r => setYears(r.data)).catch(()=>{});
    api.get("/fin-remap/bank-sources").then(r => setBanks(r.data)).catch(()=>{});
    api.get("/fin-pnl/categories")
      .then(r => setCategories([...new Set([...ALL_CATEGORIES, ...r.data])].sort()))
      .catch(()=>{});
  }, []);

  // ── fetch page ────────────────────────────────────────────────────────────
  const fetchPage = useCallback((pg) => {
    setLoading(true); setError("");
    const params = {
      limit:  PAGE_SIZE,
      offset: pg * PAGE_SIZE,
      ...(fYear   && { year:           fYear   }),
      ...(fMonth  && { month:          fMonth  }),
      ...(fBank   && { bank_source:    fBank   }),
      ...(fType   && { income_expense: fType   }),
      ...(fCat    && { category:       fCat    }),
      ...(fSearch && { search:         fSearch }),
    };
    api.get("/fin-remap/transactions", { params })
      .then(r => { setRows(r.data.rows); setTotal(r.data.total); setDirty({}); })
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [fYear, fMonth, fBank, fType, fCat, fSearch]);

  useEffect(() => { setPage(0); fetchPage(0); }, [fetchPage]);

  const goPage = (pg) => { setPage(pg); fetchPage(pg); };

  // ── inline edit helpers ───────────────────────────────────────────────────
  const markDirty = (id, field, value) => {
    const orig = rows.find(r => r.id === id) || {};
    setDirty(prev => {
      const current = prev[id] || { income_expense: orig.income_expense, category: orig.category };
      const updated = { ...current, [field]: value };
      // if both fields are back to original, remove from dirty
      if (updated.income_expense === orig.income_expense && updated.category === orig.category) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: updated };
    });
  };

  const dirtyCount = Object.keys(dirty).length;

  // ── save one row ──────────────────────────────────────────────────────────
  const saveOne = async (id) => {
    const patch = dirty[id];
    if (!patch) return;
    try {
      const res = await api.patch("/fin-remap/transactions/" + id, patch);
      // update local row so table reflects saved state
      setRows(prev => prev.map(r => r.id === id ? { ...r, ...res.data } : r));
      setDirty(prev => { const { [id]: _, ...rest } = prev; return rest; });
      setSaveMsg({ type:"success", msg:"Transaction #"+id+" saved." });
    } catch (e) {
      setSaveMsg({ type:"error", msg: e.response?.data?.detail || e.message });
    }
  };

  // ── save all dirty rows ───────────────────────────────────────────────────
  const saveAll = async () => {
    if (!dirtyCount) return;
    setSaving(true); setSaveMsg(null);
    const payload = Object.entries(dirty).map(([id, vals]) => ({
      id: Number(id),
      income_expense: vals.income_expense,
      category:       vals.category,
    }));
    try {
      const res = await api.post("/fin-remap/transactions/bulk-update", payload);
      // refresh rows to show saved state
      const updatedIds = new Set(res.data.updated);
      setRows(prev => prev.map(r => {
        if (!updatedIds.has(r.id)) return r;
        const patch = dirty[r.id];
        return patch ? { ...r, ...patch } : r;
      }));
      setDirty({});
      setSaveMsg({ type:"success", msg: res.data.count+" transaction(s) updated." });
    } catch (e) {
      setSaveMsg({ type:"error", msg: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false); }
  };

  // ── discard all changes ───────────────────────────────────────────────────
  const discardAll = () => { setDirty({}); setSaveMsg(null); };

  // ── split helpers ─────────────────────────────────────────────────────────
  const newSplitLine = (ie = "Expense", cat = "") => ({
    key:            Date.now() + Math.random(),
    amount:         "",
    income_expense: ie,
    category:       cat,
    description:    "",
  });

  const openSplit = (row) => {
    // Cancel any pending remap dirty state for this row first
    setSplitError("");
    setSplitId(row.id);
    setSplitLines([
      newSplitLine(row.income_expense, row.category),
      newSplitLine(row.income_expense, ""),
    ]);
  };

  const closeSplit = () => { setSplitId(null); setSplitLines([]); setSplitError(""); };

  const updateSplitLine = (key, field, value) => {
    setSplitLines(prev => prev.map(l => l.key === key ? { ...l, [field]: value } : l));
  };

  const addSplitLine = (origRow) => {
    setSplitLines(prev => [...prev, newSplitLine(origRow.income_expense, "")]);
  };

  const removeSplitLine = (key) => {
    setSplitLines(prev => prev.length > 2 ? prev.filter(l => l.key !== key) : prev);
  };

  // Users always type positive amounts; sign is derived from the original transaction
  const splitAbsTotal = (lines) =>
    lines.reduce((s, l) => s + Math.abs(parseFloat(l.amount) || 0), 0);

  const fillRemaining = (key, origRow) => {
    const origAbs = Math.abs(parseFloat(origRow.amount));
    const used    = splitLines.reduce((s, l) => s + (l.key !== key ? Math.abs(parseFloat(l.amount) || 0) : 0), 0);
    updateSplitLine(key, "amount", String(round2(origAbs - used)));
  };

  const confirmSplit = async (origRow) => {
    setSplitError(""); setSplitSaving(true);
    const payload = splitLines.map(l => ({
      amount:         Math.abs(parseFloat(l.amount) || 0),
      income_expense: l.income_expense,
      category:       l.category,
      description:    l.description.trim() || null,
    }));
    try {
      const res = await api.post("/fin-remap/transactions/" + origRow.id + "/split", { splits: payload });
      // Replace the original row in the table with the new split rows
      setRows(prev => {
        const idx = prev.findIndex(r => r.id === origRow.id);
        if (idx === -1) return prev;
        const next = [...prev];
        next.splice(idx, 1, ...res.data.splits);
        return next;
      });
      setTotal(t => t + res.data.splits.length - 1);
      closeSplit();
      setDirty(prev => { const { [origRow.id]: _, ...rest } = prev; return rest; });
      setSaveMsg({ type: "success", msg: "Transaction split into " + res.data.splits.length + " lines." });
    } catch (e) {
      setSplitError(e.response?.data?.detail || e.message);
    } finally {
      setSplitSaving(false);
    }
  };

  const round2 = (n) => Math.round(n * 100) / 100;

  // ── type color ────────────────────────────────────────────────────────────
  const typeColor = (ie, isDirtyRow) => {
    if (isDirtyRow) return T.orange;
    if (ie === "Income")  return T.green;
    if (ie === "Expense") return T.accentText;
    if (ie === "Equity")  return T.isDark ? "#a78bfa" : "#7c3aed";
    return T.textSub;
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const thStyle = {
    padding:"10px 14px", fontWeight:800, fontSize:9.5,
    textTransform:"uppercase", letterSpacing:"0.10em",
    color:T.textMuted, borderBottom:"1px solid "+T.divider,
    whiteSpace:"nowrap",
  };

  const selectStyle = (isDirtyField) => ({
    background: isDirtyField
      ? (T.isDark ? "rgba(251,191,36,0.12)" : "rgba(217,119,6,0.08)")
      : T.inputBg,
    border:"1px solid "+(isDirtyField ? T.orange+"88" : T.inputBdr),
    borderRadius:7, color: isDirtyField ? T.orange : T.inputText,
    fontSize:11, padding:"3px 7px", outline:"none", cursor:"pointer",
    fontFamily:T.font, fontWeight: isDirtyField ? 700 : 400,
    colorScheme: T.isDark ? "dark" : "light",
  });

  const MONTHS = [
    {v:"1",l:"Jan"},{v:"2",l:"Feb"},{v:"3",l:"Mar"},{v:"4",l:"Apr"},
    {v:"5",l:"May"},{v:"6",l:"Jun"},{v:"7",l:"Jul"},{v:"8",l:"Aug"},
    {v:"9",l:"Sep"},{v:"10",l:"Oct"},{v:"11",l:"Nov"},{v:"12",l:"Dec"},
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
        <div>
          <h2 style={{ margin:0, fontSize:15, fontWeight:800, color:T.textPrimary }}>
            Transactions
          </h2>
          <p style={{ margin:"3px 0 0", fontSize:12, color:T.textMuted }}>
            {total.toLocaleString()} transactions · re-map type and category inline
          </p>
        </div>

        {/* Save / Discard controls */}
        {dirtyCount > 0 && (
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{
              fontSize:11, fontWeight:700, color:T.orange,
              background: T.orangeBg, border:"1px solid "+T.orange+"44",
              borderRadius:8, padding:"4px 10px",
            }}>
              {dirtyCount} unsaved change{dirtyCount !== 1 ? "s" : ""}
            </span>
            <button onClick={discardAll} style={{
              fontSize:12, color:T.textMuted, background:"none",
              border:"1px solid "+T.divider, borderRadius:8,
              cursor:"pointer", padding:"6px 12px", fontFamily:T.font,
            }}>
              Discard
            </button>
            <button onClick={saveAll} disabled={saving} style={{
              fontSize:12, fontWeight:700, padding:"6px 16px", borderRadius:8,
              background: T.isDark ? "rgba(247,206,11,0.18)" : "rgba(247,206,11,0.22)",
              border:"1px solid "+T.yellow+"55",
              color:T.accentText, cursor:saving?"not-allowed":"pointer",
              opacity:saving?0.6:1, fontFamily:T.font,
            }}>
              {saving ? "Saving…" : "Save All Changes"}
            </button>
          </div>
        )}
      </div>

      {/* ── Save message ── */}
      {saveMsg && (
        <div style={{
          padding:"9px 14px", borderRadius:9, fontSize:12, fontWeight:600,
          color:      saveMsg.type==="success" ? T.green  : T.red,
          background: saveMsg.type==="success" ? T.greenBg : T.redBg,
          border:"1px solid "+(saveMsg.type==="success" ? T.green : T.red)+"44",
          display:"flex", justifyContent:"space-between", alignItems:"center",
        }}>
          {saveMsg.msg}
          <button onClick={()=>setSaveMsg(null)} style={{
            background:"none",border:"none",cursor:"pointer",
            color:T.textMuted,fontSize:16,lineHeight:1,padding:"0 0 0 12px",
          }}>×</button>
        </div>
      )}

      {/* ── Filter bar ── */}
      <div style={{
        ...gc, padding:"12px 16px",
        display:"flex", flexWrap:"wrap", gap:8, alignItems:"center",
      }}>
        <FilterSelect value={fYear}  onChange={v=>{setFYear(v);setPage(0);}}
          options={years.map(String)} placeholder="All Years" T={T} minW={100} />

        <FilterSelect value={fMonth} onChange={v=>{setFMonth(v);setPage(0);}}
          options={MONTHS.map(m=>m.v)} placeholder="All Months" T={T} minW={110}
        />

        <FilterSelect value={fBank}  onChange={v=>{setFBank(v);setPage(0);}}
          options={banks} placeholder="All Banks" T={T} minW={100} />

        <FilterSelect value={fType}  onChange={v=>{setFType(v);setPage(0);}}
          options={IE_OPTIONS} placeholder="All Types" T={T} minW={110} />

        <FilterSelect value={fCat}   onChange={v=>{setFCat(v);setPage(0);}}
          options={categories} placeholder="All Categories" T={T} minW={180} />

        {/* Search */}
        <div style={{ display:"flex", gap:0, flex:1, minWidth:200 }}>
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key==="Enter") { setFSearch(searchInput); setPage(0); } }}
            placeholder="Search description… (Enter)"
            style={{
              flex:1, background:T.inputBg, border:"1px solid "+T.inputBdr,
              borderRadius:"8px 0 0 8px", color:T.inputText,
              fontSize:12, padding:"6px 10px", outline:"none", fontFamily:T.font,
            }}
          />
          <button onClick={()=>{setFSearch(searchInput);setPage(0);}} style={{
            background: T.isDark?"rgba(247,206,11,0.14)":"rgba(247,206,11,0.18)",
            borderTop:"1px solid "+T.yellow+"44", borderRight:"1px solid "+T.yellow+"44", borderBottom:"1px solid "+T.yellow+"44", borderLeft:"none",
            borderRadius:"0 8px 8px 0", color:T.accentText,
            fontSize:13, padding:"0 12px", cursor:"pointer",
          }}>⌕</button>
          {(fSearch||fYear||fMonth||fBank||fType||fCat) && (
            <button onClick={()=>{
              setFYear("");setFMonth("");setFBank("");setFType("");setFCat("");
              setFSearch("");setSearchInput("");setPage(0);
            }} style={{
              marginLeft:6, background:"none", border:"1px solid "+T.divider,
              borderRadius:8, color:T.textMuted, fontSize:11,
              padding:"4px 10px", cursor:"pointer", fontFamily:T.font,
            }}>Clear</button>
          )}
        </div>

        <ExcelDownloadButton
          endpoint="/fin-remap/transactions/export"
          params={{ year: fYear, month: fMonth, bank_source: fBank, income_expense: fType, category: fCat, search: fSearch }}
          filename={`Transactions_${fYear || "All"}.xlsx`}
          label="Download Excel"
        />
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{
          padding:"9px 14px", borderRadius:9, fontSize:12, fontWeight:600,
          color:T.red, background:T.redBg, border:"1px solid "+T.red+"44",
        }}>{error}</div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div style={{ display:"flex", justifyContent:"center", padding:48 }}>
          <Spinner T={T} />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ ...gc, padding:56, textAlign:"center", color:T.textMuted }}>
          No transactions match the current filters.
        </div>
      ) : (
        <div style={{ ...gc, borderRadius:16, overflowX:"auto" }}>
          <table style={{ minWidth:"100%", fontSize:12, borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:"1px solid "+T.divider }}>
                {[
                  { label:"Date",        col:"date",     align:"left" },
                  { label:"Description", col:"description", align:"left" },
                  { label:"Bank",        col:"bank",     align:"left" },
                  { label:"Amount",      col:"amount",   align:"right" },
                  { label:"Type",        col:"type",     align:"left" },
                  { label:"Category",    col:"category", align:"left" },
                  { label:"Actions",     col:null,       align:"center" },
                ].map(({ label, col }) => col ? (
                  <th key={label}
                    onClick={() => handleSort(col)}
                    style={{
                      ...thStyle,
                      textAlign: label==="Amount" ? "right" : label==="Actions" ? "center" : "left",
                      color: sortKey===col ? T.yellow : undefined,
                      cursor:"pointer", userSelect:"none",
                    }}
                  >
                    {label}
                    <span style={{ marginLeft:4, opacity:sortKey===col?1:0.3, fontSize:10 }}>
                      {sortKey===col ? (sortDir==="asc" ? "▲" : "▼") : "⇅"}
                    </span>
                  </th>
                ) : (
                  <th key={label} style={{
                    ...thStyle,
                    textAlign: label==="Actions" ? "center" : "left",
                  }}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(row => {
                const d         = dirty[row.id];
                const isDirtyRow = !!d;
                const curIE     = d?.income_expense ?? row.income_expense;
                const curCat    = d?.category       ?? row.category;
                const ieChanged = d && d.income_expense !== row.income_expense;
                const catChanged= d && d.category       !== row.category;

                const isSplitting   = splitId === row.id;
                const origAbsAmt    = Math.abs(parseFloat(row.amount));
                const splitAbsSum   = isSplitting ? splitAbsTotal(splitLines) : 0;
                const remaining     = round2(origAbsAmt - splitAbsSum);

                return (
                  <React.Fragment key={row.id}>
                    {/* ── Main row ── */}
                    <tr
                      style={{
                        borderBottom: isSplitting ? "none" : "1px solid "+T.divider,
                        transition:"background 0.1s",
                        background: isSplitting
                          ? (T.isDark ? "rgba(139,92,246,0.08)" : "rgba(124,58,237,0.05)")
                          : isDirtyRow
                            ? (T.isDark?"rgba(251,191,36,0.05)":"rgba(217,119,6,0.04)")
                            : "",
                      }}
                      onMouseEnter={e => { if (!isDirtyRow && !isSplitting) e.currentTarget.style.background=T.glass; }}
                      onMouseLeave={e => { e.currentTarget.style.background = isSplitting
                        ? (T.isDark ? "rgba(139,92,246,0.08)" : "rgba(124,58,237,0.05)")
                        : isDirtyRow
                          ? (T.isDark?"rgba(251,191,36,0.05)":"rgba(217,119,6,0.04)")
                          : ""; }}
                    >
                      {/* Date */}
                      <td style={{ padding:"9px 14px", whiteSpace:"nowrap", color:T.textMuted }}>
                        {fmtDate(row.transaction_date)}
                      </td>

                      {/* Description */}
                      <td style={{
                        padding:"9px 14px", color:T.textPrimary,
                        maxWidth:260, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                      }} title={row.description}>
                        {row.description}
                      </td>

                      {/* Bank */}
                      <td style={{ padding:"9px 14px", color:T.textMuted }}>
                        {row.bank_source || "—"}
                      </td>

                      {/* Amount */}
                      <td style={{
                        padding:"9px 14px", textAlign:"right", fontWeight:700,
                        color: Number(row.amount)>=0 ? T.green : T.textSub,
                        fontVariantNumeric:"tabular-nums",
                      }}>
                        {fmt(row.amount)}
                      </td>

                      {/* Type (editable) */}
                      <td style={{ padding:"9px 14px" }}>
                        <FinancialSelect
                          value={curIE}
                          onChange={v => markDirty(row.id, "income_expense", v)}
                          options={IE_OPTIONS}
                          T={{
                            ...T,
                            inputBg:  ieChanged ? (T.isDark?"rgba(251,191,36,0.12)":"rgba(217,119,6,0.08)") : T.inputBg,
                            inputBdr: ieChanged ? T.orange+"88" : T.inputBdr,
                            inputText:ieChanged ? T.orange : typeColor(curIE, false),
                          }}
                          style={{ minWidth: 105 }}
                        />
                      </td>

                      {/* Category (editable) */}
                      <td style={{ padding:"9px 14px" }}>
                        <FinancialSelect
                          value={curCat}
                          onChange={v => markDirty(row.id, "category", v)}
                          options={[
                            ...(!categories.includes(curCat) ? [curCat] : []),
                            ...categories,
                          ]}
                          T={{
                            ...T,
                            inputBg:  catChanged ? (T.isDark?"rgba(251,191,36,0.12)":"rgba(217,119,6,0.08)") : T.inputBg,
                            inputBdr: catChanged ? T.orange+"88" : T.inputBdr,
                            inputText:catChanged ? T.orange : T.inputText,
                          }}
                          style={{ minWidth: 170 }}
                        />
                      </td>

                      {/* Actions: Save + Split */}
                      <td style={{ padding:"9px 14px", textAlign:"center", whiteSpace:"nowrap" }}>
                        <div style={{ display:"flex", gap:5, justifyContent:"center", alignItems:"center" }}>
                          {isDirtyRow && !isSplitting && (
                            <button onClick={() => saveOne(row.id)} style={{
                              fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:7,
                              background: T.isDark?"rgba(247,206,11,0.15)":"rgba(247,206,11,0.20)",
                              border:"1px solid "+T.yellow+"44",
                              color:T.accentText, cursor:"pointer", fontFamily:T.font,
                            }}>
                              Save
                            </button>
                          )}
                          {!isSplitting ? (
                            <button onClick={() => openSplit(row)} style={{
                              fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:7,
                              background: T.isDark?"rgba(139,92,246,0.15)":"rgba(124,58,237,0.08)",
                              border:"1px solid "+(T.isDark?"#8b5cf666":"#7c3aed44"),
                              color: T.isDark?"#c4b5fd":"#7c3aed",
                              cursor:"pointer", fontFamily:T.font,
                            }}>
                              Split
                            </button>
                          ) : (
                            <button onClick={closeSplit} style={{
                              fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:7,
                              background: T.isDark?"rgba(239,68,68,0.15)":"rgba(239,68,68,0.08)",
                              border:"1px solid "+(T.isDark?"#ef444466":"#ef444444"),
                              color: T.red,
                              cursor:"pointer", fontFamily:T.font,
                            }}>
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* ── Inline split panel ── */}
                    {isSplitting && (
                      <tr>
                        <td colSpan={7} style={{
                          padding:0,
                          borderBottom:"2px solid "+(T.isDark?"#8b5cf6":"#7c3aed"),
                        }}>
                          <div style={{
                            background: T.isDark ? "rgba(139,92,246,0.07)" : "rgba(124,58,237,0.04)",
                            padding:"16px 20px",
                          }}>
                            {/* Header bar */}
                            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                              <span style={{ fontSize:13, fontWeight:800, color: T.isDark?"#c4b5fd":"#7c3aed" }}>
                                Split transaction — original: {fmt(row.amount)}
                              </span>
                              <span style={{
                                fontSize:12, fontWeight:700,
                                color: Math.abs(remaining) <= 0.02 ? T.green : T.red,
                              }}>
                                Remaining: {fmt(remaining)}
                              </span>
                            </div>

                            {/* Split lines */}
                            <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:12 }}>
                              {/* Column headers */}
                              <div style={{ display:"grid", gridTemplateColumns:"130px 1fr 1fr 1fr auto auto", gap:8, alignItems:"center" }}>
                                {["Amount","Type","Category","Description (optional)","",""].map((lbl,i) => (
                                  <span key={i} style={{ fontSize:9.5, fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"0.08em" }}>
                                    {lbl}
                                  </span>
                                ))}
                              </div>

                              {splitLines.map((line, li) => (
                                <div key={line.key} style={{ display:"grid", gridTemplateColumns:"130px 1fr 1fr 1fr auto auto", gap:8, alignItems:"center" }}>
                                  {/* Amount */}
                                  <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={line.amount}
                                      onChange={e => updateSplitLine(line.key, "amount", e.target.value)}
                                      placeholder="0.00"
                                      style={{
                                        width:"100%", background:T.inputBg, border:"1px solid "+T.inputBdr,
                                        borderRadius:7, color:T.inputText, fontSize:12,
                                        padding:"5px 8px", outline:"none", fontFamily:T.font,
                                      }}
                                    />
                                    <button
                                      onClick={() => fillRemaining(line.key, row)}
                                      title="Fill remaining amount"
                                      style={{
                                        flexShrink:0, fontSize:14, lineHeight:1,
                                        background:"transparent", border:"none",
                                        color: T.isDark?"#a78bfa":"#7c3aed",
                                        cursor:"pointer", padding:"2px 4px",
                                      }}
                                    >↓</button>
                                  </div>

                                  {/* Type */}
                                  <FinancialSelect
                                    value={line.income_expense}
                                    onChange={v => updateSplitLine(line.key, "income_expense", v)}
                                    options={IE_OPTIONS}
                                    T={T}
                                    style={{ minWidth: 110 }}
                                  />

                                  {/* Category */}
                                  <FinancialSelect
                                    value={line.category}
                                    onChange={v => updateSplitLine(line.key, "category", v)}
                                    options={categories}
                                    placeholder="— select —"
                                    T={T}
                                    style={{ minWidth: 160 }}
                                  />

                                  {/* Description */}
                                  <input
                                    type="text"
                                    value={line.description}
                                    onChange={e => updateSplitLine(line.key, "description", e.target.value)}
                                    placeholder={row.description}
                                    style={{
                                      background:T.inputBg, border:"1px solid "+T.inputBdr,
                                      borderRadius:7, color:T.inputText, fontSize:12,
                                      padding:"5px 8px", outline:"none", fontFamily:T.font, width:"100%",
                                    }}
                                  />

                                  {/* Fill remaining label or spacer */}
                                  <span style={{ fontSize:10, color:T.textMuted, minWidth:16 }}>
                                    {li + 1}
                                  </span>

                                  {/* Remove line */}
                                  <button
                                    onClick={() => removeSplitLine(line.key)}
                                    disabled={splitLines.length <= 2}
                                    title="Remove this line"
                                    style={{
                                      background:"transparent", border:"none",
                                      color: splitLines.length <= 2 ? T.divider : (T.red),
                                      cursor: splitLines.length <= 2 ? "default" : "pointer",
                                      fontSize:16, lineHeight:1, padding:"0 4px",
                                    }}
                                  >×</button>
                                </div>
                              ))}
                            </div>

                            {/* Error message */}
                            {splitError && (
                              <div style={{
                                fontSize:12, color: T.red,
                                background: T.isDark?"rgba(239,68,68,0.1)":"rgba(239,68,68,0.07)",
                                border:"1px solid "+(T.isDark?"#ef444433":"#ef444422"),
                                borderRadius:8, padding:"8px 12px", marginBottom:10,
                              }}>{splitError}</div>
                            )}

                            {/* Footer buttons */}
                            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                              <button
                                onClick={() => addSplitLine(row)}
                                style={{
                                  fontSize:12, fontWeight:600, padding:"5px 14px", borderRadius:8,
                                  background:"transparent",
                                  border:"1px solid "+(T.isDark?"#8b5cf666":"#7c3aed44"),
                                  color: T.isDark?"#c4b5fd":"#7c3aed",
                                  cursor:"pointer", fontFamily:T.font,
                                }}
                              >
                                + Add Line
                              </button>

                              <button
                                onClick={() => confirmSplit(row)}
                                disabled={splitSaving || Math.abs(remaining) > 0.02}
                                style={{
                                  fontSize:12, fontWeight:700, padding:"5px 16px", borderRadius:8,
                                  background: Math.abs(remaining) <= 0.02
                                    ? (T.isDark?"rgba(34,197,94,0.2)":"rgba(22,163,74,0.12)")
                                    : T.inputBg,
                                  border:"1px solid "+(Math.abs(remaining) <= 0.02
                                    ? (T.isDark?"#22c55e88":"#16a34a55")
                                    : T.divider),
                                  color: Math.abs(remaining) <= 0.02
                                    ? T.green
                                    : T.textMuted,
                                  cursor: (splitSaving || Math.abs(remaining) > 0.02) ? "not-allowed" : "pointer",
                                  fontFamily:T.font, opacity: splitSaving ? 0.6 : 1,
                                }}
                              >
                                {splitSaving ? "Saving…" : "Confirm Split"}
                              </button>

                              <button
                                onClick={closeSplit}
                                style={{
                                  fontSize:12, fontWeight:600, padding:"5px 14px", borderRadius:8,
                                  background:"transparent", border:"1px solid "+T.divider,
                                  color:T.textMuted, cursor:"pointer", fontFamily:T.font,
                                }}
                              >
                                Cancel
                              </button>

                              <span style={{ fontSize:11, color:T.textMuted, marginLeft:4 }}>
                                {splitLines.length} line{splitLines.length !== 1 ? "s" : ""}
                                {" · "}sum: {fmt(round2(splitAbsSum))}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
          <span style={{ fontSize:12, color:T.textMuted }}>
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}
          </span>
          <div style={{ display:"flex", gap:4 }}>
            <PageBtn label="«" disabled={page===0}           onClick={()=>goPage(0)}             T={T} />
            <PageBtn label="‹" disabled={page===0}           onClick={()=>goPage(page-1)}        T={T} />
            {/* show at most 5 page numbers centred around current */}
            {Array.from({ length: totalPages }, (_,i)=>i)
              .filter(i => Math.abs(i - page) <= 2)
              .map(i => (
                <PageBtn key={i} label={i+1} active={i===page}
                  onClick={()=>goPage(i)} T={T} />
              ))}
            <PageBtn label="›" disabled={page>=totalPages-1} onClick={()=>goPage(page+1)}        T={T} />
            <PageBtn label="»" disabled={page>=totalPages-1} onClick={()=>goPage(totalPages-1)}  T={T} />
          </div>
        </div>
      )}
    </div>
  );
}

function PageBtn({ label, onClick, disabled, active, T }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      minWidth:32, height:30, padding:"0 8px", borderRadius:7,
      fontSize:12, fontWeight: active ? 800 : 500,
      background: active
        ? T.tabActiveBg
        : (T.isDark?"rgba(255,255,255,0.06)":"rgba(3,78,123,0.06)"),
      border:"1px solid "+(active ? T.yellow+"55" : T.divider),
      color: active ? T.tabActiveClr : disabled ? T.textMuted : T.textSub,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.4 : 1,
      fontFamily:T.font,
    }}>
      {label}
    </button>
  );
}
